import { getViewUnit } from "../../domain/storage/viewsStore";
import { Station, DataSampleType, DataSample, DataSampleUnit } from "../../domain/types";
import { formatDate, formatNumbers, getUnitString } from "../../domain/utils/index";
import { getWindDirectionText } from "../../domain/utils/wind";
import { getLastUpdatedSampleTimestamp, getLatestSample } from "./latestData";

export function formatData(s: Station, shortForm: boolean = false): string {
	const sections: string[] = [];
	if (!shortForm) {
		sections.push("Owner: " + s.Owner);
		if (s.Measurements.length > 0) {
			var lastSampleTimestamp = getLastUpdatedSampleTimestamp(s);
			sections.push("<i>Measured: " + formatDate(lastSampleTimestamp) + "</i>");
		}
	}
	const wind = formatWindData(s, !shortForm);
	const gust = formatWindGustData(s, !shortForm);
	const current = formatCurrentData(s, !shortForm);
	const airTemp = formatAirTempData(s, !shortForm);
	if (wind) sections.push(wind);
	if (gust) sections.push(gust);
	if (current) sections.push(current);
	if (airTemp) sections.push(airTemp);

	if (shortForm) {
		return sections.slice(0, 1).join("<br/>");
	}
	return sections.join("<br/>");
}
export function formatCurrentData(s: Station, withHelperText: boolean = true, abbreviated: boolean = false): string {
	if (!s.HasCurrentData) return "";

	const speed = getLatestSample(s, DataSampleType.CURRENT_SPEED);
	const direction = getLatestSample(s, DataSampleType.CURRENT_DIRECTION);

	const lines: string[] = [];
	if (speed && direction) {
		lines.push(`${(withHelperText ? "Current: " : "")}${formatSample(speed, !abbreviated)} ${formatSample(direction, !abbreviated)}`);
	}
	else if (speed?.Valid) {
		lines.push(`${(withHelperText ? "Current: " : "")}${formatSample(speed, !abbreviated)}`);
	}
	return lines.join("<br/>");
}
export function formatAirTempData(s: Station, withHelperText: boolean = true, abbreviated: boolean = false): string {
	if (!s.HasAirTemperatur) return "";

	const temp = getLatestSample(s, DataSampleType.AIRTEMP);
	const lines: string[] = [];
	if (temp) {
		lines.push(`${(withHelperText ? "Temperature: " : "")}${formatSample(temp, !abbreviated)}`);
	}
	return lines.join("<br/>");
}



export function formatWindData(s: Station, withHelperText: boolean = true): string {
	if (!s.HasWindData) return "";

	const speed = getLatestSample(s, DataSampleType.WIND_SPEED);
	const direction = getLatestSample(s, DataSampleType.WIND_DIRECTION);

	const lines: string[] = [];
	if (speed && direction) {
		if (withHelperText) {
			lines.push(`Wind: ${formatSample(speed)} ${formatSample(direction, true, { showText: false, abbr: false })} - ${formatSample(direction)}`);
		} else {
			lines.push(`${formatSample(speed)} ${formatSample(direction)}`);
		}
	}
	return lines.join("<br/>");
}

export function formatWindGustData(s: Station, withHelperText: boolean = true): string {
	if (!s.HasWindData) return "";

	const speed = getLatestSample(s, DataSampleType.WIND_GUST_SPEED);
	const direction = getLatestSample(s, DataSampleType.WIND_GUST_DIRECTION);

	const lines: string[] = [];
	if (speed && direction) {
		if (withHelperText) {
			lines.push(`Gust: ${formatSample(speed)} ${formatSample(direction, true, { showText: false, abbr: false })} - ${formatSample(direction)}`);
		} else {
			lines.push(`${formatSample(speed)} ${formatSample(direction)}`);
		}
	} else if (speed) {
		lines.push(`${(withHelperText ? "Gust: " : "")}${formatSample(speed)}`);
	}
	return lines.join("<br/>");
}

const invalidReplacement = "-";
export function formatSample(sample: DataSample, withUnits = true, textFormatting: { showText: boolean, abbr: boolean } | null = null): string {
	if (!sample) {
		return "N/A";
	}
	var unit = getViewUnit(sample.Type, sample.Unit);
	switch (sample.Type) {
		case DataSampleType.NONE:
			return "";
		case DataSampleType.AIRTEMP:
			return `${sample.Valid ? formatNumbers(sample.Value, 2) : invalidReplacement}${withUnits ? ` ${getUnitString(unit)}` : ""}`;
		case DataSampleType.WIND_GUST_SPEED:
		case DataSampleType.WIND_SPEED:
		case DataSampleType.CURRENT_SPEED:
			return `${sample.Valid ? formatNumbers(sample.Value, 2) : invalidReplacement}${withUnits ? ` ${getUnitString(unit)}` : ""}`;
		case DataSampleType.WIND_GUST_DIRECTION:
		case DataSampleType.WIND_DIRECTION:
		case DataSampleType.CURRENT_DIRECTION:
			if (textFormatting === null) {
				textFormatting = {
					showText: true, abbr: true
				}
			}
			if (textFormatting.showText) {
				return `${sample.Valid ? getWindDirectionText(sample.Value, textFormatting.abbr) : invalidReplacement}`
			}
			return `${sample.Valid ? formatNumbers(sample.Value, 0) : invalidReplacement}${withUnits ? `${getUnitString(unit)}` : ""}`;
		default:
			return `${sample.Valid ? formatNumbers(sample.Value, 2) : invalidReplacement}${withUnits ? ` ${getUnitString(unit)}` : ""}`;
	}
}